import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ReactComponent as CloseIcon } from '../../assets/images/close-icon.svg';
import Stepper from './Stepper';
import UpgradeStep from './UpgradeStep';
import ConfigureStep from './ConfigureStep';
import PublishingStep from './PublishingStep';
import SuccessStep from './SuccessStep';
import StripeStep from './StripeStep';
import { useIsMobile } from '../../hooks/useIsMobile';
import { AmplitudeEvent, trackEvent } from '../../utils/amplitude';

/**
 * PublishModal - Main modal component for the publish flow
 * Handles the complete flow: upgrade (if needed) → stripe (mock) → configure → publish → success
 */

type PublishStep = 'upgrade' | 'stripe' | 'configure' | 'publishing' | 'success';

type PaddleCheckoutRequest = {
  container: HTMLDivElement;
  appId: string;
  appName?: string;
  ownerEmail?: string;
};

interface PublishModalProps {
  open: boolean;
  onClose: () => void;
  appId?: string;
  appName?: string;
  isPaid: boolean;
  initialSubdomain?: string;
  onValidateSubdomain: (subdomain: string) => Promise<boolean>;
  onPublish: (subdomain: string) => Promise<string>; // Returns published URL
  onManageDomains: () => void; // Opens domain settings in dashboard
  onOpenPaddleCheckout?: (request: PaddleCheckoutRequest) => void;
  onClosePaddleCheckout?: () => void;
  isCheckoutOpen?: boolean;
  isLoadingCheckout?: boolean;
  checkoutError?: string;
  ownerEmail?: string;
}

const PublishModal: React.FC<PublishModalProps> = ({
  open,
  onClose,
  appId,
  appName,
  isPaid,
  initialSubdomain,
  onValidateSubdomain,
  onPublish,
  onManageDomains,
  onOpenPaddleCheckout,
  onClosePaddleCheckout,
  isCheckoutOpen,
  isLoadingCheckout,
  checkoutError,
  ownerEmail,
}) => {
  const [currentStep, setCurrentStep] = useState<PublishStep>(isPaid ? 'configure' : 'upgrade');
  const [publishedUrl, setPublishedUrl] = useState<string>('');
  const checkoutContainerRef = useRef<HTMLDivElement>(null);
  const checkoutWasOpenRef = useRef(false);
  const prevOpenRef = useRef(false);
  
  // Use mobile detection for conditional portal rendering
  const isMobile = useIsMobile();

  // Track modal open (edge-triggered)
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      trackEvent(AmplitudeEvent.AGENT_APP_MODAL_PUBLISH_OPEN, {
        appId,
        isPaid,
      });
    }
    prevOpenRef.current = open;
  }, [open, appId, isPaid]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setCurrentStep(isPaid ? 'configure' : 'upgrade');
      setPublishedUrl('');
    }
  }, [open, isPaid]);

  // Calculate stepper progress
  const getStepNumber = (step: PublishStep): number => {
    const steps = isPaid ? ['configure', 'publishing', 'success'] : ['upgrade', 'stripe', 'configure', 'publishing', 'success'];
    return steps.indexOf(step) + 1;
  };

  const getTotalSteps = (): number => {
    return isPaid ? 3 : 5;
  };

  // Handle upgrade click - advances to Stripe checkout step
  const handleUpgrade = useCallback(() => {
    trackEvent(AmplitudeEvent.AGENT_APP_PUBLISH_PAY_CLICK, {
      appId,
      appName,
    });
    setCurrentStep('stripe');
  }, [appId, appName]);

  // Handle Stripe cancel - goes back to upgrade step
  const handleStripeCancel = useCallback(() => {
    onClosePaddleCheckout?.();
    checkoutWasOpenRef.current = false;
    setCurrentStep('upgrade');
  }, [onClosePaddleCheckout]);

  const handleStripeReady = useCallback(() => {
    if (!onOpenPaddleCheckout || !checkoutContainerRef.current || !appId) {
      return;
    }

    if (isCheckoutOpen) {
      return;
    }

    onOpenPaddleCheckout({
      container: checkoutContainerRef.current,
      appId,
      appName,
      ownerEmail,
    });
  }, [onOpenPaddleCheckout, isCheckoutOpen, appId, appName, ownerEmail]);

  useEffect(() => {
    if (!open) {
      checkoutWasOpenRef.current = false;
      onClosePaddleCheckout?.();
    }
  }, [open, onClosePaddleCheckout]);

  useEffect(() => {
    if (currentStep !== 'stripe') {
      checkoutWasOpenRef.current = false;
      onClosePaddleCheckout?.();
    }
  }, [currentStep, onClosePaddleCheckout]);

  useEffect(() => {
    if (isCheckoutOpen) {
      checkoutWasOpenRef.current = true;
      return;
    }

    if (currentStep === 'stripe' && checkoutWasOpenRef.current) {
      checkoutWasOpenRef.current = false;
      setCurrentStep(isPaid ? 'configure' : 'upgrade');
    }
  }, [isCheckoutOpen, currentStep, isPaid, appId, appName]);

  // Handle publish
  const handlePublish = useCallback(async (subdomain: string) => {
    setCurrentStep('publishing');

    try {
      const url = await onPublish(subdomain);
      setPublishedUrl(url);
      setCurrentStep('success');
    } catch (error) {
      console.error('Publish error:', error);
      // Go back to configure step on error
      setCurrentStep('configure');
      // Error handling should be done by parent component (toast notification)
    }
  }, [onPublish]);

  // Handle close
  const handleClose = useCallback(() => {
    if (currentStep === 'publishing') {
      // Don't allow closing during publishing
      return;
    }
    onClosePaddleCheckout?.();
    checkoutWasOpenRef.current = false;
    onClose();
  }, [currentStep, onClose, onClosePaddleCheckout]);

  // Handle click outside
  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  }, [handleClose]);

  if (!open) return null;

  // On mobile during stripe step, lower z-index so Paddle checkout (z-[9999]) appears on top
  const zIndexClass = isMobile && currentStep === 'stripe' ? 'z-[9990]' : 'z-[10000]';

  const modalContent = (
    <>
      {/* Backdrop */}
      <div
        className={`fixed top-0 left-0 right-0 bottom-0 bg-black/60 backdrop-blur-sm w-full h-full ${zIndexClass}`}
        onClick={handleBackdropClick}
      />

      {/* Modal */}
      <div
        className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ${zIndexClass} animate-slide-in-center w-[calc(100vw-32px)] lg:w-auto`}
        style={{ maxWidth: '90vw', maxHeight: '90vh' }}
      >
        <div
          className="bg-[#1e293b]/80 backdrop-blur-xl border border-white/10 rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.4)] overflow-hidden w-full lg:min-w-[600px] lg:max-w-[800px]"
        >
          {/* Header with Stepper and Close Button */}
          <div className="relative bg-white/5 border-b border-white/5">
            <Stepper currentStep={getStepNumber(currentStep)} totalSteps={getTotalSteps()} />
            
            {/* Close Button */}
            {currentStep !== 'publishing' && (
              <button
                onClick={handleClose}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors duration-200"
                aria-label="Close modal"
              >
                <CloseIcon width="16" height="16" />
              </button>
            )}
          </div>

          {/* Content */}
          <div className="overflow-y-auto" style={{ maxHeight: 'calc(90vh - 80px)' }}>
            {currentStep === 'upgrade' && (
              <UpgradeStep
                onUpgrade={handleUpgrade}
                onCancel={handleClose}
                isLoading={!!isLoadingCheckout}
              />
            )}

            {currentStep === 'stripe' && (
              <div>
                <StripeStep
                  isModalOpen={!!isCheckoutOpen}
                  onModalClose={onClosePaddleCheckout}
                  onCancel={handleStripeCancel}
                  onReady={handleStripeReady}
                  isLoading={!!isLoadingCheckout}
                  errorMessage={checkoutError}
                  containerRef={checkoutContainerRef as React.RefObject<HTMLDivElement>}
                />
              </div>
            )}

            {currentStep === 'configure' && (
              <ConfigureStep
                appName={appName}
                initialSubdomain={initialSubdomain}
                onPublish={handlePublish}
                onManageDomains={onManageDomains}
                onValidateSubdomain={onValidateSubdomain}
                isPublishing={false}
              />
            )}

            {currentStep === 'publishing' && <PublishingStep />}

            {currentStep === 'success' && (
              <SuccessStep url={publishedUrl} onClose={handleClose} />
            )}
          </div>
        </div>
      </div>
    </>
  );

  // Use portal only on mobile to escape stacking context
  // On desktop, render normally to keep modal centered within AppPanel
  return isMobile ? createPortal(modalContent, document.body) : modalContent;
};

export default PublishModal;

